import Marquee from "@components/Marquee";
import { AddToPlaylistModal, AuthModal } from "@components/modals";
import LabelLink from "@components/shared/Labels/LabelLink";
import ANCHOR_IDS from "@lib/constants/anchor-ids";
import { usePlayerActions, usePlayerState } from "@lib/context/player";
import usePrevious from "@lib/hooks/usePrevious";
import { Player, STATUS } from "@lib/player";
import { Track } from "@models/track";
import { LoaderResponse } from "hls.js";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast/headless";
import {
	Artists,
	BPMInfo,
	Label,
	MixName,
	PlayInfo,
	PlayerWrapper,
	TrackInfo,
	TrackName,
	Wrapper,
} from "./Player.style";
import { Artwork } from "./artwork";
import { Clock } from "./clock";
import { Controls } from "./controls";
import { Playlist } from "./playlist";
import { Waveform } from "./waveform";

import Button from "@components/Button";
import { Box } from "@components/core/Box";
import { Modal } from "@components/core/Modal";
import { CartSelector } from "@components/interaction/CartSelector";
import { renderArtistNames } from "@components/shared/Artists/ArtistNames";
import { LayoutContext } from "@lib/context/layout/LayoutContext";
import { useSessionContext } from "@lib/context/session";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { postPlayEventRequest } from "@lib/network/events";
import { getTrackStreamRequest } from "@lib/network/tracks";
import { checkIfUserHasSubscription } from "@lib/utils";
import { firstLetterUpper } from "@lib/utils/firstLetterUpper";
import { Cart, CartModalState } from "@models/Cart";
import { IntrospectUser } from "@models/auth";
import { device } from "@styles/theme";
import debounce from "lodash/debounce";
import { Shortcuts } from "./shortcuts";
import logger from "@lib/logger";
import { dynamicImageUrl } from "@utils/dynamicImageUrl";

declare const window: any;

const SEEK_TIME = 1000;
const MAX_STREAM_AGE = 55 * 60000; // 55 minutes
// ? This is when we have the stream manifest but the stream is expired
const STREAM_EXPIRED_ERROR_CODE = 410;
// ? This is when we don't have the stream manifest (is not available anymore) and the stream is expired
const STREAM_NO_RESPONSE_ERROR_CODE = 0;

const DEFAULT_TRACK_STATS = {
	lastPlayTimestamp: 0,
	totalPlaytime: 0,
	isFinalRrecording: false,
};

const isErrorCodeCausedByOldStream = (errorCode: number | undefined) => {
	if (errorCode === undefined) return false;
	return errorCode === STREAM_EXPIRED_ERROR_CODE || errorCode === STREAM_NO_RESPONSE_ERROR_CODE;
};

const needleDropCheck = (introspectUser: IntrospectUser) => {
	try {
		if (checkIfUserHasSubscription(introspectUser)) return true;
	} catch {
		/* do nothing */
	}
	return false;
};

const fulltrackCheck = (introspectUser: IntrospectUser) => {
	return (
		introspectUser.feature.includes("feature:fulltrackplayback") &&
		(checkIfUserHasSubscription(introspectUser, "link") ||
			checkIfUserHasSubscription(introspectUser, "basic"))
	);
};

const registerPlay = async (
	{ id, stream_url, playTime, accessToken }: { id: number; stream_url: string; playTime: number; accessToken: string },
): Promise<void> => {
	try {
		const playSec = Math.floor(playTime / 1000);
		const playTimestampUnix = Math.floor(Date.now() / 1000);
		const urlObj = new URL(stream_url);
		urlObj.search = "";
		const url = urlObj.toString();
		const file = url.substring(url.lastIndexOf("/") + 1);
		const quality = file.split(".");
		quality.shift();

		const streamQuality = quality.join(".");

		await postPlayEventRequest(
			{
				device_id: 0,
				heatmap: "[]",
				item_id: id,
				offline: false,
				play_time_seconds: playSec,
				play_timestamp: playTimestampUnix,
				stream_quality: streamQuality,
			},
			accessToken,
		);
	} catch (err) {
		logger.warn(err);
	}
};

const createTimestamp = () => Date.now();

const checkIfStreamExpired = (stream_age: number | undefined) => {
	// If stream age is not set, assume it's not playing a full track stream
	if (!stream_age) return false;

	const currentTime = createTimestamp();
	const age = currentTime - stream_age;
	return age > MAX_STREAM_AGE;
};

const textAcceptingInputTypes = ["input", "textarea"];

const BPPlayer = () => {
	const playerRef = useRef<Player>();
	const [playerStatus, setPlayerStatus] = useState(STATUS.UNDEFINED);
	const [playlistVisible, setPlaylistVisible] = useState(false);
	const [currentTrackStats, setCurrentTrackStats] = useState(DEFAULT_TRACK_STATS);
	const [isLoadingError, setIsLoadingError] = useState(false);
	const [authModal, setAuthModal] = useState({
		visible: false,
	});
	const [addToPlaylistModal, setAddToPlaylistModal] = useState<{
		visible: boolean;
		id: number;
		type: string;
		tracks?: Track[];
	}>({
		visible: false,
		id: 0,
		type: "",
		tracks: [],
	});

	const showAddToPlaylist = ({ id, type, tracks }: { id: number; type: string; tracks?: Track[] }) => {
		setAddToPlaylistModal({
			visible: true,
			id: id,
			type: type,
			tracks: tracks,
		});
	};

	const handleAddToPlaylistModalOnClose = () => {
		setAddToPlaylistModal({
			id: 0,
			type: "",
			visible: false,
			tracks: [],
		});
	};

	const { currentTrack, queue, virtualQueue, shouldPlay, playerElementRef } = usePlayerState();
	const { setPlay, selectTrack, playTrack, setQueue, clearQueue, clearVirtualQueue } =
		usePlayerActions();
	const [loadingTrack, setLoadingTrack] = useState(false);
	const [shortcutsVisible, setShortcutsVisible] = useState(false);
	const [waveformZoom, setWaveformZoom] = useState(false);
	const [volume, setVolume] = useState(0.75);
	const [volumeControlVisible, setVolumeControlVisible] = useState(false);
	const isPlayingVirtualQueue = virtualQueue.length > 0;
	const queueToNavigate = isPlayingVirtualQueue ? virtualQueue : queue;

	const { getAccessToken, getIsAnon, session } = useSessionContext();
	const accessToken = getAccessToken();
	const isAnon = getIsAnon();

	const isLG = useMediaQuery({ query: device.lg });
	const isMD = useMediaQuery({ query: device.md });
	const isXSM = useMediaQuery({ query: device.xsm });

	const introspectUser = session?.introspect;

	const isFullTrackMode =
		!isAnon &&
		introspectUser &&
		needleDropCheck(introspectUser) &&
		fulltrackCheck(introspectUser) &&
		currentTrack?.is_available_for_streaming &&
		!currentTrack.pre_order;

	const isPlayerPlaying = playerStatus === STATUS.PLAYING;
	const previousPlayerStatus = usePrevious(playerStatus);
	const wasPreviouslyFullTrackMode = usePrevious(isFullTrackMode);
	const previousCurrentTrackStats = usePrevious(currentTrackStats) || 0;
	const previousTrack = usePrevious(currentTrack);
	const hasChangedTracks = previousTrack && previousTrack.id !== currentTrack?.id;

	const getFullStreamUrl = async (track: Track) => {
		const data = await getTrackStreamRequest({
			params: { start: 0, end: track.length_ms },
			id: track.id,
			accessToken,
		});

		if (data.success) {
			return data.data.stream_url;
		}

		return "";
	};

	const onHlsError = async (error: LoaderResponse) => {
		if (isErrorCodeCausedByOldStream(error?.code) && isFullTrackMode) {
			setLoadingTrack(true);
			await getNewStreamUrlAndLoadTrack(currentTrack);
			setLoadingTrack(false);
		}
	};

	const debouncedOnHlsError = useRef(debounce(onHlsError, 300)).current;

	const loadTrackIntoPlayer = async (track: Track, currentPlayTime: number) => {
		const isStreamExpired = checkIfStreamExpired(track.stream_age);

		if (isStreamExpired && isFullTrackMode) {
			setLoadingTrack(true);
			await getNewStreamUrlAndLoadTrack(track);
			setLoadingTrack(false);
			return;
		}

		if (!isFullTrackMode) {
			track = { ...track, stream_url: undefined };
		}

		try {
			await playerRef.current?.load(track);
			if (shouldPlay) {
				setTimeout(async () => {
					try {
						await playerRef.current?.play(currentPlayTime);
					} catch (error) {
						logger.error("play error:", error);
					}
				}, 100);
			}
		} catch (error: any) {
			if (error === "hlsError") {
				// Do nothing - This is being handled by the onHlsError function
				return;
			}

			toast.error(`Sorry, there was an error playing ${track.name}`, {
				duration: 2000,
			});
			setIsLoadingError(true);
			handleOnPlayNextTrackClick();
		}
	};

	const getNewStreamUrlAndLoadTrack = async (track: Track) => {
		const newStreamUrl = await getFullStreamUrl(track);
		track.stream_url = newStreamUrl;
		track.stream_age = createTimestamp();
		loadTrackIntoPlayer(track, playerRef.current?.getCurrentTime() || 0);
	};

	useEffect(() => {
		playerRef.current = new Player({ onError: debouncedOnHlsError });
		window.BPPlayer = playerRef.current; // set player as window global

		return () => playerRef.current?.playbackReset();
	}, []);

	// ? Record track stats
	useEffect(() => {
		const isPlayedInLoadedState = playerStatus === STATUS.LOADED;
		const isPlayerPaused = playerStatus === STATUS.PAUSED;
		const hasStartedPlayingNewTrack = playerStatus === STATUS.PLAYING && previousPlayerStatus === STATUS.LOADED;
		const hasPausedTrack = isPlayerPaused && previousPlayerStatus === STATUS.PLAYING;
		const hasResumedTrack = playerStatus === STATUS.PLAYING && previousPlayerStatus === STATUS.PAUSED;
		const hasChangedTracksWhilePlaying = isPlayedInLoadedState && previousPlayerStatus === STATUS.PLAYING;
		const hasChangedTracksWhilePaused = isPlayedInLoadedState && previousPlayerStatus === STATUS.PAUSED;

		const recordTrackStats = (objMutation = {}) => {
			setCurrentTrackStats((prevState) => {
				const oldStats = { ...prevState };
				const newStats = { ...DEFAULT_TRACK_STATS };

				newStats.lastPlayTimestamp = Date.now();
				const newPlaytime = newStats.lastPlayTimestamp - oldStats.lastPlayTimestamp;
				newStats.totalPlaytime = oldStats.totalPlaytime + newPlaytime;

				const newCurrentTrackStats = {
					...prevState,
					...newStats,
					...objMutation,
				};

				return newCurrentTrackStats;
			});
		};

		if (hasChangedTracksWhilePlaying) { // If the track was changed while playing, record the final stats with a new playtime
			recordTrackStats({
				isFinalRrecording: true,
			});
		} else if (hasChangedTracksWhilePaused) { // If the track was changed while paused, record the final stats with the old playtime
			recordTrackStats({
				isFinalRrecording: true,
				totalPlaytime: currentTrackStats.totalPlaytime,
			});
		} else if (hasStartedPlayingNewTrack) { // If a new track has started playing, reset the stats
			setCurrentTrackStats({
				...DEFAULT_TRACK_STATS,
				lastPlayTimestamp: Date.now(),
			});
		} else if (hasResumedTrack) { // If the track has resumed, update the last play timestamp
			setCurrentTrackStats((prevState) => ({
				...prevState,
				lastPlayTimestamp: Date.now(),
			}));
		} else if (hasPausedTrack) { // If the track has paused, record the stats
			recordTrackStats();
		}
	}, [hasChangedTracks, playerStatus, previousPlayerStatus]);

	// ? Register play event for previous track
	useEffect(() => {
		const registerPlayEvent = async () => {
			const playerTrack = playerRef.current?.getTrack();
			const playTime = currentTrackStats.totalPlaytime || 0;

			const hasPlayTime = playTime > 0;
			const isNotRepeatingEvent = currentTrackStats !== previousCurrentTrackStats;

			// Register play event for previous track
			if (
				playerTrack &&
				!playerTrack.pre_order &&
				playerTrack.is_available_for_streaming &&
				hasPlayTime &&
				isNotRepeatingEvent
			) {
				await registerPlay(
					{ id: playerTrack.id, stream_url: playerTrack.stream_url || "", playTime, accessToken },
				);
			}
		};

		if (currentTrackStats.isFinalRrecording && isFullTrackMode) {
			registerPlayEvent();
		}
	}, [currentTrackStats, previousCurrentTrackStats, isFullTrackMode, accessToken]);

	// ? Control Player status
	useEffect(() => {
		playerRef.current?.addStateChangeListener((newStatus) => {
			setPlayerStatus(newStatus);
		});

		playerRef.current?.addOnTrackEndedHandler((track) => {
			const index = track.unique_player_id ?
				queueToNavigate.map((t) => t.unique_player_id).indexOf(track.unique_player_id) :
				queueToNavigate.map((t) => t.id).indexOf(track.id);

			if (index === queueToNavigate.length - 1) {
				const t = queueToNavigate.at(0);
				if (t) {
					playTrack(t);
				}

				setPlay(false);
			} else {
				const t = queueToNavigate.at(index + 1);
				if (t) {
					setLoadingTrack(true);
					playTrack(t);
				}
			}
		});
	}, [playTrack, queueToNavigate]);

	// ? Handle play events
	useEffect(() => {
		async function play(currentTrack: Track) {
			if (!currentTrack) return;
			const playerTrack = playerRef.current?.getTrack();
			const isPlaying = playerRef.current?.isPlaying();
			const isPlayingNewTrack = playerTrack && playerTrack.unique_player_id !== currentTrack.unique_player_id;
			if (isPlayingNewTrack) setIsLoadingError(false);
			const hasJustSwitchedToFullTrackPlayback = !wasPreviouslyFullTrackMode && isFullTrackMode;
			const isResumingTrack = playerTrack && (
				(playerTrack.unique_player_id && playerTrack.unique_player_id === currentTrack.unique_player_id) ||
				(!playerTrack.unique_player_id && playerTrack.id === currentTrack.id)
			);
			setLoadingTrack(true);

			// ? If the user has access to full track
			if (
				(isFullTrackMode && isPlayingNewTrack) ||
				(hasJustSwitchedToFullTrackPlayback)
			) {
				// Get full stream URL
				const data = await getTrackStreamRequest({
					params: { start: 0, end: currentTrack.length_ms },
					id: currentTrack.id,
					accessToken,
				});

				if (data.success) {
					currentTrack.stream_url = data.data.stream_url;
					currentTrack.stream_age = createTimestamp();
				}
			}

			// ? If user is resuming an already loaded track or track had a load error, we don't need to load it again
			if (isResumingTrack && !hasJustSwitchedToFullTrackPlayback && !isLoadingError) {
				if (isPlaying) {
					if (!shouldPlay) {
						playerRef.current?.playbackPause();
					}
				} else {
					if (shouldPlay) {
						playerRef.current?.play(playerRef.current.getCurrentTime());
					}
				}
				setLoadingTrack(false);
				return;
			}

			const currentTimeSec = playerRef.current?.getCurrentTime() || 0;
			const sampleStartTimeSec = currentTrack.sample_start_ms / 1000;
			const currentPlayTime = hasJustSwitchedToFullTrackPlayback ?
				currentTimeSec + sampleStartTimeSec :
				0;

			// ? Load track into the player for first time play
			await loadTrackIntoPlayer(currentTrack, currentPlayTime);
			setLoadingTrack(false);
		}

		if (currentTrack) {
			play(currentTrack);
		}
	}, [currentTrack, isFullTrackMode, wasPreviouslyFullTrackMode, accessToken, shouldPlay]);

	const getCurrentPlayerTime = () => {
		const start = currentTrack ?
			isFullTrackMode ?
				0 :
				currentTrack.sample_start_ms :
			0;
		const play = playerRef.current ?
			playerRef.current.getCurrentTime() * 1000 :
			0;

		return start + play;
	};

	const scrollPlaylist = () => {
		setTimeout(() => {
			const playlistElement = document.querySelector(
				".player-playlist-track-current",
			);
			if (playlistElement) {
				playlistElement.scrollIntoView({ behavior: "smooth", block: "end" });
			}
		}, 200);
	};

	const handleOnKeyboardShortcutsClick = () => {
		setShortcutsVisible(!shortcutsVisible);
	};

	const handleOnVolumeClick = () => {
		setVolumeControlVisible(!volumeControlVisible);
	};

	const handleOnVolumeChange = (val: number) => {
		setVolume(val);
		playerRef.current?.setVolume(val);
	};

	const handleOnPlayPreviousTrackClick = () => {
		if (!currentTrack) return;

		const index = queueToNavigate.map((t) => t.unique_player_id).indexOf(currentTrack.unique_player_id);
		const previousTrack = queueToNavigate.at(index - 1);

		// Do not play previous track if it is the first track in the virtual queue
		if (isPlayingVirtualQueue && index <= 0) {
			return;
		}

		if (previousTrack) {
			setLoadingTrack(true);
			if (isPlayerPlaying) {
				playTrack(previousTrack);
			} else {
				selectTrack(previousTrack);
			}
		}
		scrollPlaylist();
	};

	const handleOnPlayNextTrackClick = () => {
		if (!currentTrack) return;

		const index = queueToNavigate.map((t) => t.unique_player_id).indexOf(currentTrack.unique_player_id);
		const nextTrack = queueToNavigate.at(index < queueToNavigate.length - 1 ? index + 1 : 0);

		// Do not play next track if it is the last track in the virtual queue
		if (isPlayingVirtualQueue && index + 1 >= virtualQueue.length) {
			return;
		}
		if (isLoadingError) setIsLoadingError(false);

		if (nextTrack) {
			setLoadingTrack(true);
			if (isPlayerPlaying) {
				playTrack(nextTrack);
			} else {
				selectTrack(nextTrack);
			}
		}

		scrollPlaylist();
	};

	const handleOnPlayCurrentTrackClick = async () => {
		if (currentTrack) {
			const isStreamExpired = checkIfStreamExpired(currentTrack.stream_age);

			if (isStreamExpired && isFullTrackMode) {
				const currentTime = playerRef.current?.getCurrentTime() || 0;
				setLoadingTrack(true);
				await getNewStreamUrlAndLoadTrack(currentTrack);
				setLoadingTrack(false);
				playerRef.current?.setCurrentTime(currentTime);
			}

			setPlay(true);
			playerRef.current?.playPause();
		}
	};

	const handleOnPauseCurrentTrackClick = () => {
		if (currentTrack) {
			setPlay(false);
			playerRef.current?.playPause();
		}
	};

	const handleOnTogglePlaylistClick = () => {
		setPlaylistVisible(!playlistVisible);
	};

	const handleOnRemoveTrackClick = (track: Track, index: number) => {
		queue.splice(index, 1);
		setQueue([...queue]);
	};

	const handleOnClearPlaylistClick = () => {
		setPlay(false);
		playerRef.current?.stop();

		clearQueue();
	};

	const handleOnPlaylistPlayTrackClick = (track: Track) => {
		clearVirtualQueue();
		playTrack(track);
	};

	const handleOnSeek = async (point: number) => {
		if (currentTrack) {
			const isStreamExpired = checkIfStreamExpired(currentTrack.stream_age);

			if (isStreamExpired && isFullTrackMode) {
				setLoadingTrack(true);
				await getNewStreamUrlAndLoadTrack(currentTrack);
				setLoadingTrack(false);
			}

			const start = isFullTrackMode ? 0 : currentTrack.sample_start_ms;
			const end = isFullTrackMode ?
				currentTrack.length_ms :
				currentTrack.sample_end_ms;
			if (point < end && point > start) {
				const newTime = (point - start) / 1000;
				playerRef.current?.setCurrentTime(newTime);
			}
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onKeypress = (e: KeyboardEvent) => {
		const target = e.target as HTMLElement;
		if (textAcceptingInputTypes.indexOf(target.nodeName.toLowerCase()) > -1) {
			return;
		}

		const player = playerRef.current;

		if (e.ctrlKey || e.altKey || e.metaKey) return;
		let shouldPreventDefault = true;
		const priceElement = document.querySelector("div.controls  button.add-to-cart span.price");

		switch (e.key) {
			case " ":
			case "p":
			case "P":
				if (isPlayerPlaying)
					handleOnPauseCurrentTrackClick();
				else
					handleOnPlayCurrentTrackClick();
				break;
			case "ArrowLeft":
			// case "K":
			// case "k":
				handleOnPlayPreviousTrackClick();
				break;
			case "ArrowRight":
			// case "j":
			// case "J":
				handleOnPlayNextTrackClick();
				break;
			case "i":
			case "I":
			case "o":
			case "O":
				handleOnTogglePlaylistClick();
				break;
			case "z":
			case "Z":
				setWaveformZoom(!waveformZoom);
				break;
			case ",":
			case "<":
				if (player && player?.playback && currentTrack) {
					const t = player.playback.getCurrentTime() * 1000;
					let real = t - SEEK_TIME;
					// ? Take into account the sample start time
					if (!isFullTrackMode) { real += currentTrack.sample_start_ms; }

					handleOnSeek(real);
				}
				break;
			case ".":
			case ">":
				if (player && player?.playback) {
					if (player && player?.playback && currentTrack) {
						const t = player.playback.getCurrentTime() * 1000;
						let real = t + SEEK_TIME;
						// ? Take into account the sample start time
						if (!isFullTrackMode) { real += currentTrack.sample_start_ms; }

						handleOnSeek(real);
					}
				}
				break;
			case "/":
			case "?":
				setShortcutsVisible(!shortcutsVisible);
				break;
			case "B":
			case "b":
			case "+":
			case "=":
			// setVolumeControlVisible(!volumeControlVisible);
				if (priceElement) {
					(priceElement as HTMLElement).click();
				}
				break;

			default:
				shouldPreventDefault = false;
				break;
		}

		if (shouldPreventDefault) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const [cartModal, setCartModal] = useState<CartModalState>({
		show: false,
		top: 0,
		left: 0,
		carts: [],
		selectedCarts: [],
		onSelectedCart: () => { },
	});

	const showCartSelector = ({
		show, top, left, carts, selectedCarts, onSelectedCart,
	}: {
		show: boolean;
		top: number;
		left: number;
		carts: Cart[];
		selectedCarts: number[];
		onSelectedCart: (cartId: number) => void;
	}) => {
		setCartModal({
			show: show,
			top: top,
			left: left,
			carts: carts || [],
			selectedCarts: selectedCarts,
			onSelectedCart: onSelectedCart,
		});
	};

	// keyboard shortcuts
	useEffect(() => {
		document.addEventListener("keydown", onKeypress);
		return () => document.removeEventListener("keydown", onKeypress);
	}, [onKeypress]);

	const authenticateLink = () => {
		setAuthModal({
			visible: true,
		});
	};

	const handleAuthModalOnClose = () => {
		setAuthModal({ visible: false });
	};

	const hideZoomControls: boolean =
		session !== null &&
		currentTrack !== undefined &&
		!isAnon &&
		introspectUser !== undefined &&
		needleDropCheck(introspectUser) &&
		fulltrackCheck(introspectUser) &&
		currentTrack.is_available_for_streaming;

	return (
		<div>
			<LayoutContext.Provider
				value={{
					showSidebar: false,
					setShowSidebar: () => { },
					authenticateLink,
					showCartSelector,
					showUpgradeTrackSelector: () => { },
					showAddToPlaylist,
				}}
			>
				<Wrapper
					id={ANCHOR_IDS.PLAYER}
					data-testid={ANCHOR_IDS.PLAYER}
					ref={!playlistVisible ? playerElementRef : null}
				>
					{!isLG && queue.length > 0 && playlistVisible && (
						<Playlist
							location="Player"
							tracks={queue}
							current={currentTrack}
							onRemoveTrackClick={handleOnRemoveTrackClick}
							onClearPlaylistClick={handleOnClearPlaylistClick}
							onPlaylistPlayTrackClick={handleOnPlaylistPlayTrackClick}
							setPlaylistVisible={setPlaylistVisible}
						/>
					)}
					<PlayerWrapper>
						{currentTrack && (
							<>
								{
									isXSM && (
										<Artwork
											playlist={queue}
											currentTrack={currentTrack}
											onPlay={(t) => playTrack(t)}
										/>
									)
								}
								<TrackInfo>
									<Marquee>
										<Link
											href={`/track/${currentTrack.slug}/${currentTrack.id}`}
											prefetch={false}
											title={currentTrack.name}
										>
											<TrackName>{currentTrack.name} </TrackName>
											<MixName>{currentTrack.mix_name}</MixName>
										</Link>
									</Marquee>
									{currentTrack.artists && currentTrack.artists.length > 0 && (
										<Marquee>
											<Artists>
												{renderArtistNames(currentTrack.artists, {
													location: "Player",
												})}
											</Artists>
										</Marquee>
									)}
									<Label>
										<LabelLink
											label={currentTrack.release?.label}
											location="Player"
											withMarquee
										/>
									</Label>
								</TrackInfo>
								{isMD && (
									<PlayInfo>
										<Clock
											timestamp={getCurrentPlayerTime}
											total={currentTrack.length_ms}
										/>
										<BPMInfo>{`${currentTrack.bpm} bpm`}</BPMInfo>
										<BPMInfo>{`${currentTrack.key?.name ? firstLetterUpper(currentTrack.key.name) : ""}`}</BPMInfo>
									</PlayInfo>
								)}

								{isLG && currentTrack && (
									<Waveform
										loading={loadingTrack}
										image={dynamicImageUrl({ imageUri: currentTrack.image?.uri, size: "waveform" })}
										length={currentTrack.length_ms}
										sampleStart={
											isFullTrackMode ? 0 : currentTrack.sample_start_ms
										}
										sampleEnd={
											isFullTrackMode ?
												currentTrack.length_ms :
												currentTrack.sample_end_ms
										}
										current={getCurrentPlayerTime}
										onSeek={handleOnSeek}
										zoom={waveformZoom}
										onToggleZoom={setWaveformZoom}
										showZoomControls={!hideZoomControls}
										redraw={isPlayerPlaying}
									/>
								)}
							</>
						)}
						<Controls
							track={currentTrack}
							volume={volume}
							isVolumeFaderVisible={volumeControlVisible}
							isPlaying={isPlayerPlaying}
							isLoading={loadingTrack || isLoadingError}
							playlistVisible={playlistVisible}
							onKeyboardShortcutsClick={handleOnKeyboardShortcutsClick}
							onVolumeClick={handleOnVolumeClick}
							onVolumeChange={handleOnVolumeChange}
							onPlayPreviousTrackClick={handleOnPlayPreviousTrackClick}
							onPlayCurrentTrackClick={handleOnPlayCurrentTrackClick}
							onPauseCurrentTrackClick={handleOnPauseCurrentTrackClick}
							onPlayNextTrackClick={handleOnPlayNextTrackClick}
							onTogglePlaylistClick={handleOnTogglePlaylistClick}
						/>
					</PlayerWrapper>
					{!isLG && currentTrack && (
						<Waveform
							loading={loadingTrack}
							image={dynamicImageUrl({ imageUri: currentTrack.image?.uri, size: "waveform" })}
							length={currentTrack.length_ms}
							sampleStart={isFullTrackMode ? 0 : currentTrack.sample_start_ms}
							sampleEnd={
								isFullTrackMode ?
									currentTrack.length_ms :
									currentTrack.sample_end_ms
							}
							current={getCurrentPlayerTime}
							onSeek={handleOnSeek}
							zoom={waveformZoom}
							onToggleZoom={setWaveformZoom}
							showZoomControls={!hideZoomControls}
							redraw={isPlayerPlaying}
						/>
					)}
					{isLG && queue.length > 0 && playlistVisible && (
						<Playlist
							location="Player"
							tracks={queue}
							current={currentTrack}
							onRemoveTrackClick={handleOnRemoveTrackClick}
							onClearPlaylistClick={handleOnClearPlaylistClick}
							onPlaylistPlayTrackClick={handleOnPlaylistPlayTrackClick}
							setPlaylistVisible={setPlaylistVisible}
						/>
					)}
				</Wrapper>
				<AddToPlaylistModal
					id={addToPlaylistModal.id}
					type={addToPlaylistModal.type}
					tracks={addToPlaylistModal.tracks}
					show={addToPlaylistModal.visible}
					onClose={handleAddToPlaylistModalOnClose}
				/>
			</LayoutContext.Provider>
			{cartModal.show && (
				<CartSelector
					x={cartModal.left}
					y={cartModal.top}
					carts={cartModal.carts}
					onCartSelected={cartModal.onSelectedCart}
					selectedCarts={cartModal.selectedCarts}
					onLeave={function (): void {
						showCartSelector({
							show: false,
							top: 0,
							left: 0,
							carts: [],
							selectedCarts: [],
							onSelectedCart: () => { },
						});
					}}
					alignBottom
				/>
			)}
			<Modal
				onClose={() => setShortcutsVisible(false)}
				show={shortcutsVisible}
				header="Keyboard Shortcuts"
				showClose
				footer={(
					<Box p="0" alignItems="end" width="100">
						<Button type="primary" onClick={() => setShortcutsVisible(false)}>
							Got it
						</Button>
					</Box>
				)}
			>
				<Shortcuts />
				<div style={{ justifyContent: "" }}></div>
			</Modal>
			<AuthModal
				onClose={handleAuthModalOnClose}
				show={authModal.visible}
			/>

		</div>
	);
};

export default BPPlayer;
